import { useEffect, useMemo } from 'react';
import type { CSSProperties } from 'react';

const DURATION_MS = { win: 1800, mega: 2400 } as const;
const PIECES = { win: 24, mega: 40 } as const;
const COLORS = ['#f6c453', '#38bdf8', '#86efac', '#f472b6', '#fb923c', '#e2e8f0'];
const COINS = ['🪙', '💰'];

/**
 * One burst from the centre of the amount, in front of everything, then gone.
 * Replaces the infinite emoji rain that sat between the card's background and
 * its content (half hidden by the opaque tiles, half showing through the gaps).
 */
export function CelebrationBurst({ tier, onDone }: { tier: 'win' | 'mega'; onDone: () => void }) {
  const reduced = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const pieces = useMemo(() => Array.from({ length: PIECES[tier] }, (_, index) => {
    const angle = (index / PIECES[tier]) * Math.PI * 2 + (index % 3) * 0.21;
    const distance = 140 + (index % 5) * 46;
    return {
      key: index,
      style: {
        '--dx': `${Math.round(Math.cos(angle) * distance)}px`,
        '--dy': `${Math.round(Math.sin(angle) * distance * 0.75 - 40)}px`,
        '--rot': `${(index * 47) % 360}deg`,
        '--delay': `${(index % 6) * 40}ms`,
        '--dur': `${DURATION_MS[tier]}ms`,
        background: COLORS[index % COLORS.length],
      } as CSSProperties,
      coin: tier === 'mega' && index % 4 === 0 ? COINS[index % COINS.length] : null,
    };
  }), [tier]);

  useEffect(() => {
    const timer = window.setTimeout(onDone, reduced ? 0 : DURATION_MS[tier] + 100);
    return () => window.clearTimeout(timer);
    // One-shot: re-arming on the parent's inline onDone identity would cut the burst short.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier, reduced]);

  if (reduced) return null;
  return (
    <div className="rr-burst" data-tier={tier} aria-hidden="true">
      {pieces.map((piece) => (
        <span key={piece.key} className={`rr-piece${piece.coin ? ' is-coin' : ''}`} style={piece.style}>{piece.coin}</span>
      ))}
    </div>
  );
}
